import { useState, useEffect } from 'react';
import { trustItems } from '@/mocks/homeData';
import supabase from '@/lib/supabase';

interface Stats {
  totalPosts: number;
  totalComments: number;
  totalLikes: number;
  loading: boolean;
}

export default function TrustSection() {
  const [stats, setStats] = useState<Stats>({
    totalPosts: 0,
    totalComments: 0,
    totalLikes: 0,
    loading: true,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [postsRes, commentsRes, likesRes] = await Promise.all([
        supabase.from('posts').select('*', { count: 'exact', head: true }),
        supabase.from('comments').select('*', { count: 'exact', head: true }),
        supabase.from('post_likes').select('*', { count: 'exact', head: true }),
      ]);

      setStats({
        totalPosts: postsRes.count || 0,
        totalComments: commentsRes.count || 0,
        totalLikes: likesRes.count || 0,
        loading: false,
      });
    } catch {
      setStats((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <section className="py-12 md:py-16 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <h2 className="text-lg md:text-xl font-bold text-foreground-950 text-center mb-2">
          실시간 커뮤니티 현황
        </h2>
        <p className="text-sm text-foreground-500 text-center mb-8">
          스카연 커뮤니티의 활발한 소통을 확인하세요
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <div className="bg-white rounded-lg border border-background-200 p-5 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-secondary-50 flex items-center justify-center mb-3">
              <i className="ri-article-line text-secondary-600 text-xl" />
            </div>
            {stats.loading ? (
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
            ) : (
              <span className="text-2xl font-bold text-foreground-950">
                {stats.totalPosts.toLocaleString()}
              </span>
            )}
            <p className="text-xs text-foreground-500 mt-1">총 게시글</p>
          </div>

          <div className="bg-white rounded-lg border border-background-200 p-5 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3">
              <i className="ri-chat-3-line text-accent-600 text-xl" />
            </div>
            {stats.loading ? (
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
            ) : (
              <span className="text-2xl font-bold text-foreground-950">
                {stats.totalComments.toLocaleString()}
              </span>
            )}
            <p className="text-xs text-foreground-500 mt-1">총 댓글</p>
          </div>

          <div className="bg-white rounded-lg border border-background-200 p-5 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-primary-50 flex items-center justify-center mb-3">
              <i className="ri-thumb-up-line text-primary-500 text-xl" />
            </div>
            {stats.loading ? (
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
            ) : (
              <span className="text-2xl font-bold text-foreground-950">
                {stats.totalLikes.toLocaleString()}
              </span>
            )}
            <p className="text-xs text-foreground-500 mt-1">총 추천</p>
          </div>
        </div>

        <h2 className="text-lg md:text-xl font-bold text-foreground-950 text-center mb-2 mt-12">
          신뢰할 수 있는 약속
        </h2>
        <p className="text-sm text-foreground-500 text-center mb-8">
          스카연이 지키는 핵심 원칙입니다
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {trustItems.map((item) => (
            <div
              key={item.title}
              className="bg-white rounded-lg border border-background-200 p-5 text-center hover:border-primary-300 transition-colors"
            >
              <div className="w-12 h-12 mx-auto rounded-full bg-secondary-50 flex items-center justify-center mb-3">
                <i className={`${item.icon} text-secondary-600 text-xl`} />
              </div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-1">{item.title}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed whitespace-pre-line">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}